export const AUTH_FLOW_COOKIE_NAME = "cognito_auth_flow";
export const AUTH_FLOW_COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 days

export type AuthFlowType = "sp" | "idp";

/**
 * Sets a cookie to track the authentication flow type (SP-initiated vs IDP-initiated).
 *
 * Security Note: This cookie does NOT have the HttpOnly attribute because it's set
 * via client-side JavaScript (document.cookie). HttpOnly can only be set by server-side
 * HTTP responses. However, this cookie only contains non-sensitive flow type information
 * ('sp' or 'idp'), not authentication tokens, so the lack of HttpOnly is acceptable.
 *
 * The 'secure' flag is enabled for all non-localhost environments (dev, staging, prod),
 * ensuring HTTPS-only transmission wherever HTTPS is available.
 *
 * For sensitive authentication tokens (access tokens, refresh tokens), see the
 * AWS Amplify CookieStorage configuration in providers/auth.tsx which handles
 * secure token storage.
 */
export function setAuthFlowCookie(flow: AuthFlowType): void {
  const isLocal = location.hostname === "localhost";
  const cookieString =
    `${AUTH_FLOW_COOKIE_NAME}=${flow}; ` +
    `path=/; ` +
    `max-age=${AUTH_FLOW_COOKIE_MAX_AGE}; ` +
    `${isLocal ? "" : "secure; "}` +
    `samesite=strict`;

  document.cookie = cookieString;
}

export function getAuthFlowCookie(): AuthFlowType | null {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === AUTH_FLOW_COOKIE_NAME) {
      return value as AuthFlowType;
    }
  }
  return null;
}

export function clearAuthFlowCookie(): void {
  document.cookie = `${AUTH_FLOW_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}
